import { supabase } from '../config/supabase'

class OfferService {
  /**
   * Fetch all active offers
   */
  async getActiveOffers() {
    try {
      const now = new Date().toISOString()
      
      const { data, error } = await supabase
        .from('offers')
        .select('*')
        .eq('is_active', true)
        .or(`valid_until.is.null,valid_until.gte.${now}`)
        .lte('valid_from', now)
        .order('priority', { ascending: false })

      if (error) throw error
      return data || []
    } catch (error) {
      console.error('Error fetching active offers:', error)
      return []
    }
  }

  /**
   * Get customer's order statistics for eligibility checking
   * Updated to support guest users via sessionId and phone
   */
  async getCustomerStats(customerId, sessionId = null, customerPhone = null) {
    try {
      let orders = []
      let isGuest = false

      // For users with customer_id: fetch orders and check if guest
      if (customerId) {
        console.log('📊 Fetching stats for customer:', customerId)
        
        // First, check if this customer is a guest
        const { data: customerData, error: customerError } = await supabase
          .from('customers')
          .select('is_guest')
          .eq('id', customerId)
          .single()
        
        if (customerError) {
          console.error('❌ Error fetching customer data:', customerError)
        }
        
        isGuest = customerData?.is_guest === true
        console.log(`📊 Customer ${customerId} is ${isGuest ? 'GUEST' : 'AUTHENTICATED'}`)
        
        // Fetch orders
        const { data, error } = await supabase
          .from('orders')
          .select('total_amount, status')
          .eq('customer_id', customerId)
          .not('status', 'eq', 'cancelled')

        if (error) throw error
        orders = data || []
      }
      // For guest users: fetch by session_id or customer_phone
      else if (sessionId || customerPhone) {
        console.log('📊 Fetching stats for guest user:', { sessionId, customerPhone })
        
        let query = supabase
          .from('orders')
          .select('id, session_id, customer_phone, customer_name, total_amount, status, created_at')
          .not('status', 'eq', 'cancelled')

        // Prefer session_id if available
        if (sessionId) {
          console.log('🔍 Querying orders by session_id:', sessionId)
          query = query.eq('session_id', sessionId)
        } else if (customerPhone) {
          // Fallback to customer_phone for guest orders
          console.log('🔍 Querying orders by customer_phone:', customerPhone)
          query = query.eq('customer_phone', customerPhone)
        }

        const { data, error } = await query
        if (error) {
          console.error('❌ Error fetching guest orders:', error)
          throw error
        }
        orders = data || []
        isGuest = true
        
        console.log(`📦 Found ${orders.length} orders for guest user:`, orders.map(o => ({
          id: o.id,
          session_id: o.session_id,
          customer_phone: o.customer_phone,
          total_amount: o.total_amount
        })))
      }
      // No identifier provided
      else {
        console.log('⚠️ No customer identifier provided')
        return {
          totalOrders: 0,
          totalSpent: 0,
          avgOrderValue: 0,
          isNewUser: true,
          isGuest: true
        }
      }

      const totalOrders = orders?.length || 0
      const totalSpent = orders?.reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0) || 0
      const avgOrderValue = totalOrders > 0 ? totalSpent / totalOrders : 0

      console.log('📊 Customer stats calculated:', {
        totalOrders,
        totalSpent,
        avgOrderValue,
        isNewUser: totalOrders === 0,
        isGuest
      })

      return {
        totalOrders,
        totalSpent,
        avgOrderValue,
        isNewUser: totalOrders === 0,
        isGuest
      }
    } catch (error) {
      console.error('Error fetching customer stats:', error)
      return {
        totalOrders: 0,
        totalSpent: 0,
        avgOrderValue: 0,
        isNewUser: true,
        isGuest: true
      }
    }
  }

  /**
   * Get customer's usage count for a specific offer
   * Updated to support guest users via sessionId and phone
   */
  async getOfferUsageCount(customerId, offerId, sessionId = null, customerPhone = null) {
    try {
      // For all users (logged-in and guest): Check customer_offers table
      if (customerId) {
        console.log(`🔍 Checking usage count for customer ${customerId}, offer ${offerId}`)

        const { data, error } = await supabase
          .from('customer_offers')
          .select('id')
          .eq('customer_id', customerId)
          .eq('offer_id', offerId)

        if (error) {
          console.error('❌ Error fetching offer usage:', error)
          throw error
        }
        
        const usageCount = data?.length || 0
        console.log(`📊 Usage count result: ${usageCount} uses found for this specific offer`)
        return usageCount
      }
      
      // For guest users without customer_id: Cannot track offer usage reliably
      // This should not happen as guest users get a customer_id after first order
      if (sessionId || customerPhone) {
        console.log(`⚠️ Guest user without customer_id - cannot track offer usage accurately`)
        console.log(`   This is expected for first-time guests only`)
        return 0
      }
      
      console.log(`⚠️ No identifier provided for usage check`)
      return 0
    } catch (error) {
      console.error('Error fetching offer usage:', error)
      return 0
    }
  }

  /**
   * Check if customer is eligible for an offer
   * Updated to support guest users
   */
  async isEligibleForOffer(offer, customerId, cartSubtotal, sessionId = null, customerPhone = null) {
    try {
      console.log(`🔍 Checking eligibility for offer: ${offer.offer_code}`, {
        customerId,
        cartSubtotal,
        offerDetails: {
          is_active: offer.is_active,
          target_user_type: offer.target_user_type,
          min_order_amount: offer.min_order_amount,
          valid_from: offer.valid_from,
          valid_until: offer.valid_until
        }
      })

      // Check if offer is active
      if (!offer.is_active) {
        console.log(`❌ ${offer.offer_code} - Not active`)
        return false
      }

      // Check validity dates
      const now = new Date()
      const validFrom = new Date(offer.valid_from)
      const validUntil = offer.valid_until ? new Date(offer.valid_until) : null

      if (now < validFrom) {
        console.log(`❌ ${offer.offer_code} - Not yet valid (starts ${validFrom})`)
        return false
      }
      if (validUntil && now > validUntil) {
        console.log(`❌ ${offer.offer_code} - Expired (ended ${validUntil})`)
        return false
      }

      // Check minimum order amount
      if (offer.min_order_amount && cartSubtotal < offer.min_order_amount) {
        console.log(`❌ ${offer.offer_code} - Cart subtotal ${cartSubtotal} < min ${offer.min_order_amount}`)
        return false
      }

      // Get customer stats (supports both logged-in and guest users)
      const customerStats = await this.getCustomerStats(customerId, sessionId, customerPhone)
      console.log(`📊 Customer stats for ${customerId || 'guest'}:`, customerStats)

      // Special handling for first order offers (FIRST_ORDER_10, FIRST_ORDER_25)
      // These should ONLY be available for AUTHENTICATED customers placing their very first order
      // NOT available for guest users
      if (offer.offer_code === 'FIRST_ORDER_10' || offer.offer_code === 'FIRST_ORDER_25') {
        // Must be authenticated (not guest)
        if (customerStats.isGuest || !customerId) {
          console.log(`🚫 ${offer.offer_code} not available - customer is guest or not logged in`)
          return false
        }
        // Must be a new user (first order)
        if (!customerStats.isNewUser) {
          console.log(`🚫 ${offer.offer_code} not available - customer is not new (has ${customerStats.totalOrders} orders)`)
          return false
        }
      }

      // Check user type targeting
      if (offer.target_user_type === 'new_user') {
        // New user offers are ONLY for authenticated users placing their first order
        // NOT for guest users
        if (customerStats.isGuest || !customerId) {
          console.log(`❌ ${offer.offer_code} - Target is new_user but customer is guest or not logged in`)
          return false
        }
        if (!customerStats.isNewUser) {
          console.log(`❌ ${offer.offer_code} - Target is new_user but customer is not new`)
          return false
        }
      }

      if (offer.target_user_type === 'existing_user') {
        // Existing user offers are ONLY for authenticated users with previous orders
        // NOT for guest users
        if (customerStats.isGuest || !customerId) {
          console.log(`❌ ${offer.offer_code} - Target is existing_user but customer is guest or not logged in`)
          return false
        }
        if (customerStats.isNewUser) {
          console.log(`❌ ${offer.offer_code} - Target is existing_user but customer is new`)
          return false
        }
      }

      if (offer.target_user_type === 'guest_user' && !customerStats.isGuest) {
        console.log(`❌ ${offer.offer_code} - Target is guest_user but customer is not guest`)
        return false
      }

      // For existing users, check advanced filters
      if (offer.target_user_type === 'existing_user') {
        // Check average order value
        if (offer.min_avg_order_value && customerStats.avgOrderValue < offer.min_avg_order_value) {
          return false
        }
        if (offer.max_avg_order_value && customerStats.avgOrderValue > offer.max_avg_order_value) {
          return false
        }

        // Check lifetime spend
        if (offer.min_lifetime_spend && customerStats.totalSpent < offer.min_lifetime_spend) {
          return false
        }
        if (offer.max_lifetime_spend && customerStats.totalSpent > offer.max_lifetime_spend) {
          return false
        }

        // Check total orders
        if (offer.min_total_orders && customerStats.totalOrders < offer.min_total_orders) {
          return false
        }
        if (offer.max_total_orders && customerStats.totalOrders > offer.max_total_orders) {
          return false
        }
      }

      // Check usage limit per customer (for both logged-in and guest users)
      if (offer.usage_limit_per_customer) {
        const usageCount = await this.getOfferUsageCount(customerId, offer.id, sessionId, customerPhone)
        console.log(`📊 ${offer.offer_code} - Usage count: ${usageCount}/${offer.usage_limit_per_customer}`)
        
        if (usageCount >= offer.usage_limit_per_customer) {
          console.log(`❌ ${offer.offer_code} - Usage limit reached (${usageCount}/${offer.usage_limit_per_customer})`)
          return false
        }
      } else if (!offer.usage_limit_per_customer) {
        // If no explicit limit is set, log a warning
        console.log(`⚠️ ${offer.offer_code} - No usage_limit_per_customer set, assuming unlimited use`)
      }

      // Check total usage limit (optional - would need a count of all uses)
      // This is a platform-wide limit, not per customer

      console.log(`✅ ${offer.offer_code} - Customer is ELIGIBLE!`)
      return true
    } catch (error) {
      console.error('Error checking offer eligibility:', error)
      return false
    }
  }

  /**
   * Get all eligible offers for a customer
   * Updated to support guest users
   */
  async getEligibleOffers(customerId, cartSubtotal, sessionId = null, customerPhone = null) {
    try {
      const allOffers = await this.getActiveOffers()
      const eligibleOffers = []

      for (const offer of allOffers) {
        const isEligible = await this.isEligibleForOffer(offer, customerId, cartSubtotal, sessionId, customerPhone)
        if (isEligible) {
          eligibleOffers.push(offer)
        }
      }

      return eligibleOffers
    } catch (error) {
      console.error('Error getting eligible offers:', error)
      return []
    }
  }

  /**
   * Calculate discount amount for an offer
   */
  calculateDiscount(offer, cartSubtotal) {
    let discountAmount = 0

    if (offer.discount_type === 'percentage') {
      discountAmount = (cartSubtotal * offer.discount_value) / 100
      
      // Apply max discount cap if set
      if (offer.max_discount_amount && discountAmount > offer.max_discount_amount) {
        discountAmount = offer.max_discount_amount
      }
    } else if (offer.discount_type === 'fixed') {
      discountAmount = offer.discount_value
    }

    // Ensure discount doesn't exceed cart total
    return Math.min(discountAmount, cartSubtotal)
  }

  /**
   * Record offer usage when order is placed
   */
  async recordOfferUsage(customerId, offerId, orderId, discountAmount) {
    try {
      if (!customerId || !offerId) return

      const { error } = await supabase
        .from('customer_offers')
        .insert([{
          customer_id: customerId,
          offer_id: offerId,
          order_id: orderId,
          discount_amount: discountAmount,
          used_at: new Date().toISOString()
        }])

      if (error) throw error
    } catch (error) {
      console.error('Error recording offer usage:', error)
      throw error
    }
  }

  /**
   * Validate and apply offer code
   */
  async validateOfferCode(offerCode, customerId, cartSubtotal) {
    try {
      // Fetch offer by code
      const { data: offers, error } = await supabase
        .from('offers')
        .select('*')
        .eq('offer_code', offerCode.toUpperCase())
        .eq('is_active', true)
        .limit(1)

      if (error) throw error
      if (!offers || offers.length === 0) {
        return { valid: false, message: 'Invalid offer code' }
      }

      const offer = offers[0]

      // Check eligibility
      const isEligible = await this.isEligibleForOffer(offer, customerId, cartSubtotal)
      
      if (!isEligible) {
        return { valid: false, message: 'You are not eligible for this offer' }
      }

      // Calculate discount
      const discountAmount = this.calculateDiscount(offer, cartSubtotal)

      return {
        valid: true,
        offer,
        discountAmount,
        message: 'Offer applied successfully!'
      }
    } catch (error) {
      console.error('Error validating offer code:', error)
      return { valid: false, message: 'Error validating offer code' }
    }
  }
}

export default new OfferService()
